import React, { useState, useEffect } from 'react';
import axios from 'axios';
import WidgetRenderer from './WidgetRenderer';
import WaveLoader from './WaveLoader';

/**
 * Renders custom widgets assigned to a specific page.
 * Placed in App.jsx after the main page component — existing pages need no changes.
 *
 * Props:
 *   pageId  - the current module ID (e.g., 'sales-home', 'agri-operations')
 *   filters - global filters (unused here but available for future use)
 */
function PageWidgets({ pageId }) {
  const [widgets, setWidgets] = useState([]);
  const [widgetData, setWidgetData] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!pageId || pageId === 'custom-widgets') return;

    setLoading(true);
    axios.get(`/api/widgets?page_id=${encodeURIComponent(pageId)}`)
      .then(res => {
        const list = res.data.widgets || [];
        setWidgets(list);
        // Fetch data for each widget
        return Promise.all(
          list.map(w =>
            axios.get(`/api/widgets/${w.id}/data`)
              .then(r => ({ id: w.id, data: r.data.data, viz_config: r.data.viz_config }))
              .catch(() => ({ id: w.id, data: null, viz_config: null }))
          )
        );
      })
      .then(results => {
        const dataMap = {};
        (results || []).forEach(r => { dataMap[r.id] = r; });
        setWidgetData(dataMap);
      })
      .catch(err => console.error('Error loading page widgets:', err))
      .finally(() => setLoading(false));
  }, [pageId]);

  if (!widgets.length && !loading) return null;
  if (loading && !widgets.length) return null;

  return (
    <div style={{ marginTop: '24px' }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: '8px',
        marginBottom: '16px', paddingBottom: '8px',
        borderBottom: '1px solid #e0e0e0',
      }}>
        <span style={{ fontSize: '14px', fontWeight: 600, color: '#6c757d' }}>Custom Widgets</span>
      </div>
      <div className="chart-grid" style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))',
        gap: '16px',
      }}>
        {widgets.map(w => {
          const wd = widgetData[w.id];
          return (
            <div key={w.id} className="chart-card" style={{
              background: '#fff',
              borderRadius: '12px',
              padding: '16px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            }}>
              {wd && wd.data ? (
                <WidgetRenderer data={wd.data} vizConfig={wd.viz_config} />
              ) : (
                <div style={{ padding: '40px', textAlign: 'center', color: '#999' }}>
                  {loading ? <WaveLoader size="small" /> : 'Failed to load widget data'}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default PageWidgets;
